import { useEffect, useState } from "react"
import { Head, Link, router, usePoll } from "@inertiajs/react"
import {
  AlertTriangle, ArrowDown, ArrowUp, BookOpen, Bot, CheckCircle2, Copy, Download, FileText, Globe, Layers, Loader2, Mail, Plus, Printer, Send, ShieldCheck, Upload, User, Video,
} from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { CHAPTER_STATUS, STATUS_ORDER, StatusChip, formatDate } from "./components/bookStatus"
import { LeaderBadge } from "./components/LeaderCard"
import { DeleteBookDialog } from "@/components/book/DeleteBookDialog"

const SOURCE_ICONS = { meeting: Video, article: FileText, upload: Upload, website: Globe }

// What the coach should do next for a chapter, by status.
function nextAction(bookId, chapter) {
  const base = `/app/coach/books/${bookId}/chapters/${chapter.id}`
  if (chapter.generationError) return { text: "See what went wrong", href: base, primary: true }
  switch (chapter.status) {
    case "needs_info": return { text: "Add missing info", href: `${base}/edit`, primary: true }
    case "ready_to_draft": return { text: "Draft chapter", href: `${base}/edit`, primary: true }
    case "ready_for_review": return { text: "Review", href: base, primary: true }
    case "approved": return { text: "Read", href: base }
    default: return { text: "The AI is writing", href: base }
  }
}

function initials(name) {
  return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()
}

export default function BookShow({ currentUser, book, chapters = [], parts, assemblyBlockers = [], printBlockers = [], assemblies = [], sent, brand, canAddChapter, generating, ownAi }) {
  const { start, stop } = usePoll(8000, { only: ["chapters", "parts", "assemblyBlockers", "printBlockers", "assemblies", "brand", "generating"] }, { autoStart: false })
  const building = assemblies.some((a) => a.building)
  useEffect(() => {
    if (generating) start()
    else stop()
    return () => stop()
  }, [generating])

  const approved = chapters.filter((c) => c.status === "approved").length
  const counts = STATUS_ORDER.reduce((acc, key) => ({ ...acc, [key]: chapters.filter((c) => c.status === key).length }), {})
  const [min, max] = book.chapterRange
  const move = (chapter, direction) =>
    router.patch(`/app/coach/books/${book.id}/chapters/${chapter.id}/move`, { direction }, { preserveScroll: true })

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${book.title} - Kingdom Factor`} />
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-kf-red">My Kingdom Factors book</div>
            <h1 className="text-2xl font-bold text-zinc-900">{book.title}</h1>
            <p className="text-sm text-zinc-600">
              By {book.author} · A book has {min} to {max} chapters · 5.5 × 8.5 in ·{" "}
              <Link href="/app/coach/books?all=1" className="underline underline-offset-4">My books</Link>
            </p>
          </div>
          {canAddChapter && (
            <Button asChild>
              <Link href={`/app/coach/books/${book.id}/chapters/new`}>
                <Plus className="h-4 w-4" /> Add chapter
              </Link>
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="grid gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <div className="mb-2 flex justify-between text-sm">
                <span><b>{approved} of {chapters.length}</b> chapters approved</span>
                <span className="text-zinc-500">{chapters.length} of {max} chapters added</span>
              </div>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-zinc-100">
                {chapters.length > 0 && STATUS_ORDER.map((key) => counts[key] > 0 && (
                  <span key={key} className={CHAPTER_STATUS[key].bar} style={{ width: `${(counts[key] / chapters.length) * 100}%` }} />
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600">
                {STATUS_ORDER.map((key) => (
                  <span key={key} className="inline-flex items-center gap-1">
                    <i className={`inline-block h-2 w-2 rounded-full ${CHAPTER_STATUS[key].bar}`} />
                    {CHAPTER_STATUS[key].label} {counts[key]}
                  </span>
                ))}
              </div>
            </div>
            <div className="max-w-xs space-y-2">
              <Button className="w-full" variant={assemblyBlockers.length ? "outline" : "default"} disabled={assemblyBlockers.length > 0 || building}
                onClick={() => router.post(`/app/coach/books/${book.id}/assemble`)}>
                {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} {building ? "Assembling…" : assemblies.length ? "Assemble again" : "Assemble book"}
              </Button>
              <div className="text-xs text-zinc-600">
                {assemblyBlockers.length > 0 ? (
                  <><AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-amber-600" /><b>Not yet.</b> {assemblyBlockers.join(" ")}</>
                ) : (
                  "Ready. This builds the print PDFs to proof. You can assemble as often as you like."
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={chapters.length ? "gap-0 overflow-hidden py-0" : undefined}>
          {chapters.length === 0 ? (
            <CardContent className="py-12 text-center">
              <BookOpen className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
              <p className="font-medium text-zinc-900">No chapters yet</p>
              <p className="mb-4 text-sm text-zinc-600">Add the first leader. You need their interview, a headshot, and their Life Verse.</p>
              {canAddChapter && (
                <Button asChild><Link href={`/app/coach/books/${book.id}/chapters/new`}><Plus className="h-4 w-4" /> Add chapter</Link></Button>
              )}
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="w-16 px-4 py-3">#</th>
                    <th className="px-4 py-3">Chapter</th>
                    <th className="px-4 py-3">Profiled leader</th>
                    <th className="px-4 py-3">Sources</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Last change</th>
                    <th className="px-4 py-3">Next action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {chapters.map((chapter, index) => {
                    const next = nextAction(book.id, chapter)
                    return (
                      <tr key={chapter.id} className="align-top">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 font-semibold text-zinc-900">
                            {index + 1}
                            {!book.sentToPrint && (
                              <span className="flex flex-col">
                                <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => move(chapter, "up")} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                                <button type="button" aria-label="Move down" disabled={index === chapters.length - 1} onClick={() => move(chapter, "down")} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Link href={`/app/coach/books/${book.id}/chapters/${chapter.id}`} className="font-medium text-zinc-900 hover:text-kf-red">
                            {chapter.title || <span className="italic text-zinc-500">Untitled (the AI picks a title)</span>}
                          </Link>
                          {chapter.missingInfo.length > 0 && (
                            <div className="mt-1 text-xs text-amber-700">Needs {chapter.missingInfo.join(", ")}.</div>
                          )}
                          {chapter.generationError && <div className="mt-1 text-xs text-red-600">{chapter.generationError}</div>}
                          {chapter.brandFlags > 0 && (
                            <Link href={`/app/coach/books/${book.id}/chapters/${chapter.id}#brand-check`}
                              className="mt-1 flex items-center gap-1 text-xs text-red-700 underline-offset-4 hover:underline">
                              <ShieldCheck className="h-3.5 w-3.5" /> {chapter.brandFlags} Brand check {chapter.brandFlags === 1 ? "flag" : "flags"} to look at
                            </Link>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-xs font-semibold text-zinc-700">{initials(chapter.leaderName)}</span>
                            <div>
                              <Link href={`/app/coach/books/${book.id}/chapters/${chapter.id}/edit`} title="Edit leader details"
                                className="font-medium text-zinc-900 hover:text-kf-red">{chapter.leaderName}</Link>
                              <div className="text-xs text-zinc-500">{[chapter.leaderRole, chapter.leaderCompany].filter(Boolean).join(", ")}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 text-zinc-500">
                            {chapter.sourceKinds.map((kind) => {
                              const Icon = SOURCE_ICONS[kind]
                              return <Icon key={kind} className="h-4 w-4" aria-label={kind} />
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-start gap-1">
                            <StatusChip status={chapter.status} />
                            {chapter.status === "approved" && <LeaderBadge status={chapter.leaderStatus} />}
                            {chapter.leaderNote && chapter.leaderStatus !== "approved" && <span className="text-xs text-zinc-600">The leader sent a note</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-500">{formatDate(chapter.updatedAt)}</td>
                        <td className="px-4 py-3">
                          <Button asChild size="sm" variant={next.primary ? "default" : "outline"}>
                            <Link href={next.href}>{next.text}</Link>
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 text-sm">
            <ShieldCheck className="h-5 w-5 text-kf-red" />
            <div className="min-w-0 flex-1">
              <b>Brand check</b>
              <div className="text-zinc-600">
                {brand.parts === 0
                  ? "Each chapter is checked against Kingdom Factor's Statement of Faith and Core Values when you approve it."
                  : [
                      `${brand.checked} of ${brand.parts} parts checked.`,
                      brand.open ? `${brand.open} ${brand.open === 1 ? "flag" : "flags"} to look at. Open the chapter or part to read each flag, why it was flagged, and a suggested fix.` : null,
                      brand.awaitingAdmin ? `${brand.awaitingAdmin} serious ${brand.awaitingAdmin === 1 ? "flag waits" : "flags wait"} for Kingdom Factor to decide.` : null,
                      !brand.open && !brand.awaitingAdmin ? "No open flags." : null,
                    ].filter(Boolean).join(" ")}
              </div>
            </div>
            {brand.checking ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking</span>
            ) : (
              <Button variant="outline" size="sm" disabled={book.sentToPrint || brand.checked === brand.parts}
                onClick={() => router.post(`/app/coach/books/${book.id}/brand_check`)}>
                Check the rest of the book
              </Button>
            )}
          </CardContent>
        </Card>

        {(assemblies.length > 0 || sent) && (
          <PrintCard book={book} assemblies={assemblies} printBlockers={printBlockers} sent={sent} />
        )}

        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-500">Book parts that are not chapters</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <PartCard
              icon={Mail}
              title="Author letter"
              status={parts.letter.approved ? "Approved" : parts.letter.drafting ? "The AI is writing" : parts.letter.drafted ? "Ready for review" : "Not started"}
              done={parts.letter.approved}
              href={`/app/coach/books/${book.id}/letter`}
              text="A Few Words from the Author. The AI drafts it from your chapters; you edit and approve it."
            />
            <PartCard
              icon={User}
              title="About the Author"
              status={parts.about.done ? "Done" : parts.about.started ? "Started" : "Not started"}
              done={parts.about.done}
              href={`/app/coach/books/${book.id}/about`}
              text="Your bio and photo for the back of the book."
            />
            <PartCard
              icon={Layers}
              title="Cover"
              status={parts.cover.approved ? "Approved" : "Not approved"}
              done={parts.cover.approved}
              href={`/app/coach/books/${book.id}/cover`}
              text="Pick a Kingdom Factor cover design and check the words about you."
            />
          </div>
        </div>

        {ownAi && <OwnAiCard {...ownAi} />}

        {!book.sentToPrint && (
          <div className="flex justify-end">
            <DeleteBookDialog title={book.title} path={`/app/coach/books/${book.id}`} />
          </div>
        )}
      </div>
    </CoachLayout>
  )
}

// The assemblies (every one kept) and the one final step: Send to print.
function PrintCard({ book, assemblies, printBlockers, sent }) {
  const send = () => {
    if (window.confirm("Send this book to print? The latest assembly goes to Kingdom Factor for the printer, and the book locks. Only Kingdom Factor can unlock it.")) {
      router.post(`/app/coach/books/${book.id}/send_to_print`)
    }
  }
  return (
    <Card>
      <CardContent className="space-y-4 text-sm">
        <div className="flex items-center gap-2"><Printer className="h-5 w-5 text-kf-red" /><b>Print files</b></div>
        <div className="divide-y rounded-md border">
          {assemblies.map((assembly) => (
            <div key={assembly.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div><b>Assembly {assembly.number}</b> · <span className="text-zinc-500">{formatDate(assembly.createdAt)}</span></div>
                <div className="text-xs text-zinc-600">
                  {assembly.building && <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Building the PDFs. About a minute.</span>}
                  {assembly.failed && <span className="text-red-600">{assembly.error}</span>}
                  {assembly.pageCount && `${assembly.pageCount} pages · 5.5 × 8.5 in · spine ${assembly.spineWidth.toFixed(3)} in · `}
                  {assembly.pageCount && (assembly.current ? <span className="text-emerald-700">matches the book now</span> : <span className="text-amber-700">the book changed since</span>)}
                </div>
              </div>
              {assembly.interiorUrl && (
                <Button asChild size="sm" variant="outline"><a href={assembly.interiorUrl}><Download className="h-4 w-4" /> Interior</a></Button>
              )}
              {assembly.coverUrl && (
                <Button asChild size="sm" variant="outline"><a href={assembly.coverUrl}><Download className="h-4 w-4" /> Cover</a></Button>
              )}
            </div>
          ))}
        </div>
        {sent ? (
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">
            <CheckCircle2 className="h-4 w-4" /> Sent to print on {formatDate(sent.at)} (assembly {sent.assembly}). The book is locked.
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={printBlockers.length > 0} onClick={send}><Send className="h-4 w-4" /> Send to print</Button>
            <span className="flex-1 text-xs text-zinc-600">
              {printBlockers.length > 0 ? <><b>Not yet.</b> {printBlockers.join(" ")}</> : "Read both PDFs first. Sending locks the book."}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// The coach's own Claude or ChatGPT on the Kingdom Factor connector
// (ADR-0017): it reads the book and the coach's sources and saves drafts the
// coach approves here.
function OwnAiCard({ connectorUrl, skillUrl }) {
  const [copied, setCopied] = useState(false)
  const copy = () => navigator.clipboard.writeText(connectorUrl).then(() => setCopied(true))

  return (
    <Card>
      <CardContent className="space-y-4 text-sm">
        <div className="flex items-center gap-2"><Bot className="h-5 w-5 text-kf-red" /><b>Use your own AI</b></div>
        <p className="text-zinc-600">
          Connect Claude, ChatGPT, or another AI app that supports custom connectors. Your AI can then read your book, your Meetings, and your Articles, and save drafts here. A draft from your AI shows as “From your AI”, and you approve it like any other version. It does not use your three AI changes.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="rounded border bg-zinc-50 px-2 py-1 text-xs">{connectorUrl}</code>
          <Button size="sm" variant="outline" onClick={copy}><Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy the address"}</Button>
          <Button asChild size="sm" variant="outline"><a href={skillUrl}><Download className="h-4 w-4" /> Download the KF Book skill</a></Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="font-medium text-zinc-900">Claude</div>
            <ol className="mt-1 list-decimal space-y-1 pl-5 text-zinc-600">
              <li>In Claude, open Settings, then Connectors, then Add custom connector.</li>
              <li>Name it Kingdom Factor and paste the address above.</li>
              <li>Click Connect. Sign in with your Kingdom Factor email and click Authorize.</li>
              <li>Optional: in Settings, then Capabilities, upload the KF Book skill.</li>
            </ol>
          </div>
          <div>
            <div className="font-medium text-zinc-900">ChatGPT</div>
            <ol className="mt-1 list-decimal space-y-1 pl-5 text-zinc-600">
              <li>In ChatGPT, open Plugins, select the plus button, then Add custom MCP server.</li>
              <li>Name it Kingdom Factor and paste the address above. Pick OAuth.</li>
              <li>Sign in with your Kingdom Factor email and click Authorize.</li>
              <li>Optional: paste the KF Book skill's text into a ChatGPT project's instructions.</li>
            </ol>
          </div>
        </div>
        <p className="text-xs text-zinc-500">Then ask, for example: “Read my chapter about Dana and rewrite the Crucible Story from the interview.”</p>
      </CardContent>
    </Card>
  )
}

function PartCard({ icon: Icon, title, status, done, href, text }) {
  const body = (
    <Card className={`h-full ${href ? "transition-colors hover:border-kf-red" : "opacity-70"}`}>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-kf-red" />
          <span className="font-semibold text-zinc-900">{title}</span>
          <span className={`ml-auto inline-flex items-center gap-1 text-xs ${done ? "text-emerald-700" : "text-zinc-500"}`}>
            {done && <CheckCircle2 className="h-3.5 w-3.5" />}
            {status}
          </span>
        </div>
        <p className="text-sm text-zinc-600">{text}</p>
      </CardContent>
    </Card>
  )
  return href ? <Link href={href}>{body}</Link> : body
}
